import { DOCUMENT } from '@angular/common';
import { inject, Injectable, InjectionToken, signal } from '@angular/core';
import type { LiveUpdatePlugin, ReadyResult } from '@capawesome/capacitor-live-update';
import { buildInfo } from '../../../buildinfo';
import { environment } from '../../../environments/environment';
import { isNativeShell } from '../utils/native-shell';
import { LoggerService } from './logger.service';
import { ToastService } from './toast.service';
import { TranslationService } from './translation.service';

/**
 * Per-channel manifest written by mobile/scripts/live-update-publish.mjs and
 * served as a static file (see docs/features/capacitor-live-updates.md).
 */
export interface LiveUpdateManifest {
  schemaVersion: 1;
  channel: string;
  bundleId: string;
  /** Zip URL — absolute, or relative to the manifest URL */
  url: string;
  /** SHA-256 of the zip, hex */
  checksum: string;
  /** RSA PKCS#1 v1.5 / SHA-256 signature of the zip, base64 — verified natively */
  signature: string;
  /** Lowest native build (versionCode / CFBundleVersion) this bundle runs on */
  minNativeVersionCode: number;
}

export interface LiveUpdateConfig {
  manifestUrl: string;
  /** Minimum time between checks (startup + each return to foreground) */
  checkIntervalMs?: number;
}

export type LiveUpdateDecision =
  | 'download'
  | 'already-downloaded'
  | 'current'
  | 'pending'
  | 'blocked'
  | 'native-too-old';

const DEFAULT_CHECK_INTERVAL_MS = 15 * 60 * 1000;
/** How long the "Updating…" overlay shows before reloading — a local reload is otherwise invisible. */
export const RESTART_OVERLAY_MS = 800;
const UPDATED_TOAST_MS = 8000;

/**
 * Bundle id for a build: `<yyyyMMddHHmmss>-<gitHash>`, derived from buildinfo.
 * Must match bundleIdFor() in mobile/scripts/live-update-publish.mjs.
 */
export function liveUpdateBundleId(info: { timestamp: string; gitHash: string }): string {
  return `${info.timestamp.replace(/[^0-9]/g, '').slice(0, 14)}-${info.gitHash}`;
}

/**
 * Loads the plugin module. Resolves to the module, NOT the plugin itself: a
 * Capacitor plugin is a proxy that treats every property — including `then` —
 * as a native method, so resolving a promise with it fails with
 * '"LiveUpdate.then()" is not implemented'.
 */
export const LIVE_UPDATE_PLUGIN = new InjectionToken<
  () => Promise<{ LiveUpdate: LiveUpdatePlugin }>
>('LIVE_UPDATE_PLUGIN', {
  providedIn: 'root',
  // Lazy import keeps the plugin out of the web bundle's initial chunk.
  factory: () => () => import('@capawesome/capacitor-live-update'),
});

export const LIVE_UPDATE_CONFIG = new InjectionToken<LiveUpdateConfig | null>(
  'LIVE_UPDATE_CONFIG',
  {
    providedIn: 'root',
    factory: () =>
      ((environment as Record<string, unknown>)['liveUpdate'] as LiveUpdateConfig | undefined) ??
      null,
  },
);

export const IS_NATIVE_SHELL = new InjectionToken<boolean>('IS_NATIVE_SHELL', {
  providedIn: 'root',
  factory: () => isNativeShell(),
});

/**
 * Over-the-air web bundle updates inside the Capacitor shell (no-op on the web,
 * where the Angular service worker handles updates).
 *
 * Policy:
 * - `start()` runs once the app shell has rendered and calls `ready()` first, so a
 *   bundle that renders is confirmed healthy; one that doesn't is rolled back
 *   natively after `readyTimeout` and blocked.
 * - Updates are downloaded in the background and applied on the next cold start
 *   (or when the user accepts the restart prompt) — never on app resume, which
 *   would reload mid-OAuth or mid-form when the app backgrounds for the system
 *   browser, camera or file chooser.
 */
@Injectable({ providedIn: 'root' })
export class LiveUpdateService {
  private readonly log = inject(LoggerService).getLogger('live-update');
  private readonly loadPlugin = inject(LIVE_UPDATE_PLUGIN);
  private readonly config = inject(LIVE_UPDATE_CONFIG);
  private readonly isNative = inject(IS_NATIVE_SHELL);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslationService);
  private readonly document = inject(DOCUMENT);

  private readonly restartingState = signal(false);
  /** True while the restart overlay is showing (App renders it). */
  readonly restarting = this.restartingState.asReadonly();

  private plugin: LiveUpdatePlugin | null = null;
  private lastCheckAt = 0;
  private checking = false;
  private promptShown = false;

  async start(): Promise<void> {
    if (!this.isNative || this.plugin) return;

    try {
      const { LiveUpdate: plugin } = await this.loadPlugin();
      const ready = await plugin.ready();
      this.plugin = plugin;
      if (ready.rollback) {
        this.log.warn(
          `Rolled back from bundle ${ready.previousBundleId} to ${ready.currentBundleId ?? 'built-in'}`,
        );
      }
      this.announceAppliedUpdate(ready);
      const { versionCode } = await plugin.getVersionCode();
      this.log.info(
        `Running bundle=${ready.currentBundleId ?? 'built-in'} ` +
          `build=${liveUpdateBundleId(buildInfo)} native=${versionCode}`,
      );
    } catch (error) {
      this.log.error('Live update init failed:', error);
      return;
    }

    if (!this.config) {
      this.log.warn('No liveUpdate config for this build — update checks disabled');
      return;
    }

    this.document.addEventListener('visibilitychange', () => {
      if (this.document.visibilityState === 'visible') void this.checkForUpdate();
    });
    await this.checkForUpdate();
  }

  async checkForUpdate(): Promise<LiveUpdateDecision | null> {
    const plugin = this.plugin;
    const config = this.config;
    if (!plugin || !config || this.checking) return null;
    if (Date.now() - this.lastCheckAt < (config.checkIntervalMs ?? DEFAULT_CHECK_INTERVAL_MS)) {
      return null;
    }

    this.checking = true;
    this.lastCheckAt = Date.now();
    try {
      const manifest = await this.fetchManifest(config.manifestUrl);
      if (!manifest) return null;

      const decision = await this.decide(plugin, manifest);
      this.log.debug(`Manifest bundle ${manifest.bundleId}: ${decision}`);
      if (decision !== 'download' && decision !== 'already-downloaded') return decision;

      if (decision === 'download') {
        await plugin.downloadBundle({
          artifactType: 'zip',
          bundleId: manifest.bundleId,
          url: new URL(manifest.url, config.manifestUrl).toString(),
          checksum: manifest.checksum,
          signature: manifest.signature,
        });
      }
      await plugin.setNextBundle({ bundleId: manifest.bundleId });
      this.log.info(`Bundle ${manifest.bundleId} downloaded — applies on next start`);
      this.promptRestart(plugin);
      return decision;
    } catch (error) {
      // Offline, server down, or signature/checksum rejected natively.
      this.log.warn('Live update check failed:', error);
      return null;
    } finally {
      this.checking = false;
    }
  }

  private async decide(
    plugin: LiveUpdatePlugin,
    manifest: LiveUpdateManifest,
  ): Promise<LiveUpdateDecision> {
    const [current, next, blocked, bundles, native] = await Promise.all([
      plugin.getCurrentBundle(),
      plugin.getNextBundle(),
      plugin.getBlockedBundles(),
      plugin.getBundles(),
      plugin.getVersionCode(),
    ]);

    if (manifest.bundleId === liveUpdateBundleId(buildInfo)) return 'current';
    if (manifest.bundleId === current.bundleId) return 'current';
    if (manifest.bundleId === next.bundleId) return 'pending';
    if (blocked.bundleIds.includes(manifest.bundleId)) return 'blocked';
    const nativeVersion = Number.parseInt(native.versionCode, 10);
    if (!(nativeVersion >= manifest.minNativeVersionCode)) return 'native-too-old';
    if (bundles.bundleIds.includes(manifest.bundleId)) return 'already-downloaded';
    return 'download';
  }

  private async fetchManifest(url: string): Promise<LiveUpdateManifest | null> {
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) {
      this.log.warn(`Manifest fetch returned ${response.status}`);
      return null;
    }
    const manifest = (await response.json()) as Partial<LiveUpdateManifest>;
    const valid =
      manifest.schemaVersion === 1 &&
      typeof manifest.bundleId === 'string' &&
      typeof manifest.url === 'string' &&
      typeof manifest.checksum === 'string' &&
      typeof manifest.signature === 'string' &&
      typeof manifest.minNativeVersionCode === 'number';
    if (!valid) {
      this.log.warn('Ignoring malformed live update manifest');
      return null;
    }
    return manifest as LiveUpdateManifest;
  }

  private promptRestart(plugin: LiveUpdatePlugin): void {
    if (this.promptShown) return;
    this.promptShown = true;
    this.toast.info(this.translate.translate('liveUpdate.ready'), false, 0, [
      {
        label: this.translate.translate('liveUpdate.restart'),
        onClick: () => {
          this.log.info('User accepted live update restart');
          void this.restartNow(plugin);
        },
      },
      {
        label: this.translate.translate('liveUpdate.later'),
        onClick: () => this.log.info('User deferred live update to next start'),
      },
    ]);
  }

  /** Shows the "Updating…" overlay, then reloads into the staged bundle. */
  private async restartNow(plugin: LiveUpdatePlugin): Promise<void> {
    this.restartingState.set(true);
    await new Promise((resolve) => setTimeout(resolve, RESTART_OVERLAY_MS));
    try {
      await plugin.reload(); // on success the page is replaced and this never returns
    } catch (error) {
      this.log.error('Live update reload failed:', error);
      this.restartingState.set(false);
      this.toast.error(this.translate.translate('liveUpdate.restartFailed'), false, 0);
    }
  }

  /**
   * After launch, tells the user what happened to an update: applied (toast with
   * the new build) or rolled back. Covers both "Restart now" and a plain reopen.
   * ready() reports the bundle recorded at the previous ready() call, so a
   * difference means this launch is running a different bundle than last time.
   */
  private announceAppliedUpdate(ready: ReadyResult): void {
    if (ready.rollback) {
      this.toast.warning(this.translate.translate('liveUpdate.rolledBack'), false, 0);
    } else if (ready.currentBundleId !== ready.previousBundleId) {
      this.log.info(
        `Update applied: ${ready.previousBundleId ?? 'built-in'} → ${ready.currentBundleId ?? 'built-in'}`,
      );
      const builtAt = new Date(buildInfo.timestamp).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
      this.toast.success(
        this.translate.translate('liveUpdate.updated', { version: `${builtAt} (${buildInfo.gitHash})` }),
        true,
        UPDATED_TOAST_MS,
      );
    }
  }
}
